import { useEffect, useState, useRef } from 'react';
import { useInView } from 'framer-motion';

/**
 * NumberTicker — animates a number from 0 → value when scrolled into view.
 *
 * Usage:
 *   <NumberTicker value={2750} prefix="" suffix="" duration={1.8} />
 *
 * Props:
 *  - value:    final number (required)
 *  - duration: animation duration in seconds (default 1.6)
 *  - prefix / suffix
 *  - decimals: number of decimal places (default 0)
 *  - className
 */
export default function NumberTicker({
  value = 0,
  duration = 1.6,
  prefix = '',
  suffix = '',
  decimals = 0,
  className = '',
  ...rest
}) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-40px' });
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (!inView) return;
    let raf;
    const t0 = performance.now();
    const tick = (t) => {
      const p = Math.min(1, (t - t0) / (duration * 1000));
      // ease-out cubic for that satisfying deceleration
      const eased = 1 - Math.pow(1 - p, 3);
      setDisplay(value * eased);
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, value, duration]);

  const formatted = decimals > 0
    ? display.toFixed(decimals)
    : Math.round(display).toLocaleString();

  return (
    <span ref={ref} className={`tabular-nums ${className}`} {...rest}>
      {prefix}{formatted}{suffix}
    </span>
  );
}
